import React from 'react';
import { clearReloadFlag, isChunkLoadError, reloadOnceForChunkError } from '../../utils/lazyLoad';
import './ChunkErrorBoundary.css';

/**
 * The boundary the lazy workspace screens render inside, ported from
 * hr-portal-frontend (src/components/ChunkErrorBoundary.js).
 *
 * A chunk error is almost always a tab that outlived a deploy, so it auto-reloads
 * once per session; if that guard has already fired, this renders instead of
 * looping. Its sheet is the one workspace sheet outside the workspace chunk,
 * because the chunk is what it exists to survive; it wears the brand where the
 * tokens are there and neutral literals where they are not.
 */
class ChunkErrorBoundary extends React.Component {
    state = { error: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error) {
        reloadOnceForChunkError(error);
    }

    componentDidUpdate(previousProps) {
        // A route change is a fresh attempt: clear the error so the back button
        // does not land on the failure of the screen the user just left.
        if (this.state.error && previousProps.pathname !== this.props.pathname) {
            this.setState({ error: null });
        }
    }

    handleReload = () => {
        clearReloadFlag();
        window.location.reload();
    };

    render() {
        if (!this.state.error) return this.props.children;

        const stale = isChunkLoadError(this.state.error);

        return (
            <div className="ws-fault" role="alert">
                <h2 className="ws-fault-title">
                    {stale ? 'There is a newer version' : 'This screen did not load'}
                </h2>
                <p className="ws-fault-body">
                    {stale
                        ? 'Reload to pick it up. Nothing you have saved is affected.'
                        : 'Reloading usually fixes it. Nothing you have saved is affected.'}
                </p>
                <button type="button" className="ws-fault-action" onClick={this.handleReload}>
                    Reload
                </button>
            </div>
        );
    }
}

export default ChunkErrorBoundary;
